import { VFC, useMemo, ComponentType, memo, Suspense } from 'react'
import { isMemo } from 'react-is'
import { isFunction } from '../../framework/utils'
import { useThemeOverride, useResetPageTitle } from '../common/appEffects'
import ErrorBoundary from '../common/ErrorBoundary'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { useMemoProps, useGetThemeOverride, useAutoCollapse } from './RouteWrapper'
import type { ComponentWrapperProps } from '../../framework/microAppTypes'

const isNoopFn = (fn: any) => {
  if (!isFunction(fn)) {
    return false
  }
  if (fn.name === 'noop' || '__noop__' in fn) {
    return true
  }
  const fnStr = fn.toString().replace(/\/\/.*?\n|\/\*[\s\S]*?\*\//g, '').replace(/\s|;/g, '')
  const isNoop = /\)\{\}$|=>(?:\{\}|null|undefined|void0)$|\{return(?:null|undefined|void0)?\}$/.test(fnStr)
  if (isNoop && !Object.isFrozen(fn)) {
    Object.defineProperty(fn, '__noop__', { value: isNoop })
  }
  return isNoop
}

export const MemoComponentWrapper: VFC<ComponentWrapperProps> = ({ navItem, renderProps, parentProps }) => {
  const isStateFnNoop = useMemo(() => isNoopFn(navItem.mapStateToProps), [navItem])
  const routeProps = useMemo(() => {
    if (isStateFnNoop) {
      return // NSUP-44
    }
    const { state, appBar, sideMenuDrawer, ...routeProps } = parentProps // ignore those always changing
    const props = navItem.mapStateToProps?.(state)
    if (navItem.mapStateToProps && ((props == null && !navItem.mapStateToProps.length) || String(props) === 'memo')) {
      return // NSUP-44
    }
    Object.assign(routeProps, props ?? {
      // main: state.main, // remove as deprecated
      auth: state.auth,
      theme: state.theme,
      vars: navItem.appVars,
    })
    return routeProps
  }, [navItem, parentProps, isStateFnNoop])
  const props = useMemoProps(useMemo(() => ({
    ...renderProps,
    ...routeProps,
    navItem: navItem,
  }), [renderProps, routeProps, navItem]))
  const NavComp: ComponentType = navItem.component as ComponentType
  const MemoNavComp: ComponentType = useMemo(() => {
    return NavComp.prototype?.isReactComponent || isMemo(NavComp) ? NavComp : memo(NavComp)
  }, [NavComp])
  const errorContext = navItem.microAppId || navItem.appName
  useThemeOverride(useGetThemeOverride(navItem))
  useAutoCollapse(navItem)
  useResetPageTitle()
  return (
    <ErrorBoundary context={errorContext}>
      <Suspense fallback={<LoadingPlaceholder />}>
        <MemoNavComp {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

export default MemoComponentWrapper
